import { useRuntimeConfig } from "#app"
import { currentAccessToken, refreshAccessToken, restRequest } from "./restClient"
import { clearSession, loadSession, saveSession } from "./tokenStore"
import { BrowserUnsupportedCommandError } from "../ClientSdk"
import type { AuthResponse } from "~/composables/useAuth"
import { boardRoutes } from "./boardRoutes"

type Args = Record<string, unknown>
export type RouteHandler = (args: Args) => Promise<unknown>

const OAUTH_REDIRECT_URI_KEY = "iopta:oauth-redirect-uri"

function payload(args: Args): Args {
  return (args.payload as Args | undefined) ?? {}
}

// The frontend's `Role.permissions` (`app/types/groups.ts`) is `number[]` — Rust/gRPC splits the
// bitmask across one or more int64 chunks. The REST API's `Role.permissions` (see
// docs.iopta.org/docs/api/introduction/groups#role) is a single int64 instead — wrap it so
// `permissionsToMask`/`effectivePermissionMask` (`~/lib/permissions.ts`), written against the
// array shape, keep working unmodified.
function normalizeRole(role: Record<string, unknown>): Record<string, unknown> {
  // proto3 JSON leaves `false` out, so a role that isn't hoisted arrives without the field.
  const hoist = role.hoist === true
  const permissions = role.permissions
  if (Array.isArray(permissions)) return { ...role, hoist }
  return { ...role, hoist, permissions: permissions === undefined ? [] : [permissions] }
}

// Numeric encoding Rust's `update_user_status` command takes (see
// `app/composables/useUserStatus.ts`'s `STATUS_CODES`) — inferred from that existing mapping,
// not independently confirmed against the REST API's `userStatus` enum. Revisit once the real
// backend contract for this endpoint is confirmed.
const USER_STATUS_BY_CODE: Record<number, string> = {
  0: "ONLINE",
  1: "OFFLINE",
  2: "DO_NOT_DISTURB",
  3: "INVISIBLE",
}

// `page_size`/`page_token` query string for the paginated group lists. Built by hand rather than
// through `restRequest`'s `query` option: these two parameters are snake_case on the wire (that
// option camelCases its keys), and the token must be percent-encoded — it's opaque and can contain
// `+`, `/` and `=`.
function pageQuery(args: Args): string {
  const params: string[] = []
  if (args.pageSize) params.push(`page_size=${encodeURIComponent(String(args.pageSize))}`)
  if (args.pageToken) params.push(`page_token=${encodeURIComponent(String(args.pageToken))}`)
  return params.length ? `?${params.join("&")}` : ""
}

function asRecords(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []
}

// int64 fields arrive as JSON strings over REST; the Tauri path hands back numbers.
function usageTotals(value: unknown): Record<string, number> {
  const t = (value ?? {}) as Record<string, unknown>
  return {
    requests: Number(t.requests ?? 0),
    input_tokens: Number(t.input_tokens ?? 0),
    output_tokens: Number(t.output_tokens ?? 0),
    cost_micro: Number(t.cost_micro ?? 0),
    charged_micro: Number(t.charged_micro ?? 0),
  }
}

// Proto enums come back over REST as their names ("AGENT_TASK_KIND_CRON"); the Tauri path (and
// the frontend types) use the numbers. Zero-valued enums may be omitted from the JSON entirely.
const AGENT_TASK_KIND_NAMES = ["AGENT_TASK_KIND_ONCE", "AGENT_TASK_KIND_CRON", "AGENT_TASK_KIND_INTERVAL"]
const AGENT_TASK_STATUS_NAMES = [
  "AGENT_TASK_STATUS_ACTIVE", "AGENT_TASK_STATUS_PAUSED", "AGENT_TASK_STATUS_COMPLETED", "AGENT_TASK_STATUS_CANCELLED",
]
const AGENT_TASK_RUN_STATUS_NAMES = [
  "AGENT_TASK_RUN_STATUS_NONE", "AGENT_TASK_RUN_STATUS_RUNNING", "AGENT_TASK_RUN_STATUS_SUCCEEDED",
  "AGENT_TASK_RUN_STATUS_FAILED", "AGENT_TASK_RUN_STATUS_SKIPPED", "AGENT_TASK_RUN_STATUS_MISSED",
]

function enumValue(value: unknown, names: string[]): number {
  if (typeof value === "number") return value
  if (typeof value === "string") {
    const index = names.indexOf(value)
    if (index >= 0) return index
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return 0
}

const AGENT_TOOL_APPROVAL_SOURCE_NAMES = [
  "AGENT_TOOL_APPROVAL_SOURCE_UNSPECIFIED", "AGENT_TOOL_APPROVAL_SOURCE_CHAT", "AGENT_TOOL_APPROVAL_SOURCE_SCHEDULED",
  "AGENT_TOOL_APPROVAL_SOURCE_BOARD_TASK", "AGENT_TOOL_APPROVAL_SOURCE_AGENT",
]
const AGENT_TOOL_APPROVAL_STATUS_NAMES = [
  "AGENT_TOOL_APPROVAL_STATUS_UNSPECIFIED", "AGENT_TOOL_APPROVAL_STATUS_PENDING", "AGENT_TOOL_APPROVAL_STATUS_APPROVED",
  "AGENT_TOOL_APPROVAL_STATUS_DENIED", "AGENT_TOOL_APPROVAL_STATUS_EXECUTED", "AGENT_TOOL_APPROVAL_STATUS_FAILED",
  "AGENT_TOOL_APPROVAL_STATUS_EXPIRED",
]

/** REST `AgentToolApproval` -> the `AgentToolApprovalDto` shape the Tauri command returns. */
export function normalizeAgentToolApproval(value: unknown): Record<string, unknown> {
  const a = (value ?? {}) as Record<string, unknown>
  const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v))
  return {
    id: str(a.id),
    bot_id: str(a.bot_id),
    bot_name: str(a.bot_name),
    bot_avatar_url: str(a.bot_avatar_url),
    server_id: str(a.server_id),
    server_name: str(a.server_name),
    tool_name: str(a.tool_name),
    tool_title: str(a.tool_title),
    arguments_json: str(a.arguments_json),
    source: enumValue(a.source, AGENT_TOOL_APPROVAL_SOURCE_NAMES),
    group_id: str(a.group_id),
    channel_id: str(a.channel_id),
    channel_name: str(a.channel_name),
    board_card_id: str(a.board_card_id),
    board_card_title: str(a.board_card_title),
    initiator_id: str(a.initiator_id),
    initiator_name: str(a.initiator_name),
    status: enumValue(a.status, AGENT_TOOL_APPROVAL_STATUS_NAMES),
    created_at: a.created_at ?? null,
    expires_at: a.expires_at ?? null,
  }
}

function normalizeAgentTask(value: unknown): Record<string, unknown> {
  const t = (value ?? {}) as Record<string, unknown>
  return {
    id: String(t.id ?? ""),
    bot_id: String(t.bot_id ?? ""),
    author_id: String(t.author_id ?? ""),
    channel_id: String(t.channel_id ?? ""),
    group_id: String(t.group_id ?? ""),
    title: String(t.title ?? ""),
    prompt: String(t.prompt ?? ""),
    kind: enumValue(t.kind, AGENT_TASK_KIND_NAMES),
    cron_expression: t.cron_expression ?? null,
    interval_seconds: Number(t.interval_seconds ?? 0),
    run_at: t.run_at ?? null,
    time_zone: String(t.time_zone ?? ""),
    status: enumValue(t.status, AGENT_TASK_STATUS_NAMES),
    next_run_at: t.next_run_at ?? null,
    last_run_at: t.last_run_at ?? null,
    last_status: enumValue(t.last_status, AGENT_TASK_RUN_STATUS_NAMES),
    last_error: t.last_error ?? null,
    runs_count: Number(t.runs_count ?? 0),
    created_by_agent: !!t.created_by_agent,
    created_at: t.created_at ?? null,
    updated_at: t.updated_at ?? null,
  }
}

/**
 * cmd -> REST call, mirroring `src-tauri/src/commands/*.rs` one-for-one wherever
 * docs.iopta.org/docs/api documents an equivalent endpoint. Args/return shapes match what the
 * Tauri command of the same name accepts/returns — see `app/lib/platform/browser/caseConvert.ts`
 * for how the camelCase <-> snake_case boundary is handled so this table can just deal in the
 * same snake_case shapes the stores/composables already use.
 *
 * Commands with no documented REST endpoint (bots, interactions, agent credentials, the native
 * screen/system-audio capture, the on-disk media/attachment cache, the desktop updater, sound
 * playback) are simply absent — `BrowserClientSdk` rejects them with
 * `BrowserUnsupportedCommandError`.
 */
export const routes: Record<string, RouteHandler> = {
  // --- auth / session (docs.iopta.org/docs/api/introduction/anonymous-api, .../oauth2) ---

  // Tauri's version opens a system browser + local callback server (see
  // `src-tauri/src/commands/auth.rs`); there's no equivalent in a plain tab, so this does a
  // normal full-page OAuth2 authorization-code redirect instead. `redirectUri` is whatever the
  // caller wants Keycloak to send the user back to: an explicit `?redirect_uri=` on the current
  // page if one was given (e.g. an external site deep-linking into login), otherwise the current
  // page's own address — so login just returns the user to wherever they clicked it from. It's
  // sent to the authorize-url request and stashed so the code-exchange step can echo the exact
  // same value back, per the API's requirement that it "match exactly the URI used when obtaining
  // the code".
  start_oauth_login: async () => {
    // `window.location.origin` is what the browser itself observed, which is wrong behind a
    // reverse proxy/different public hostname than the container sees requests on — `appOrigin`
    // (settable per-container via `NUXT_PUBLIC_APP_ORIGIN`, see `nuxt.config.ts`) overrides it
    // when set. An explicit `?redirect_uri=` on the current page wins over both.
    const configuredOrigin = useRuntimeConfig().public.appOrigin
    const redirectUri =
      new URLSearchParams(window.location.search).get("redirect_uri") ||
      `${configuredOrigin || window.location.origin}${window.location.pathname}`
    localStorage.setItem(OAUTH_REDIRECT_URI_KEY, redirectUri)

    const { url } = await restRequest<{ url: string }>("/v1/anonymous/authorize-url", {
      auth: false,
      query: { redirect_uri: redirectUri },
    })

    // The server doesn't actually honor the `redirect_uri` query param above — it hands back a
    // fixed authorize URL with no `redirect_uri` on it at all — so set/overwrite it ourselves
    // before navigating, otherwise Keycloak falls back to whatever its client's own default is
    // (observed: `account`'s), which isn't this app.
    const authorizeUrl = new URL(url)
    authorizeUrl.searchParams.set("redirect_uri", redirectUri)

    console.log("[OAuth] redirect_uri:", redirectUri)
    console.log("[OAuth] redirecting to:", authorizeUrl.toString())
    window.location.href = authorizeUrl.toString()
    // Navigation away interrupts execution before this ever matters to a caller.
    return new Promise(() => undefined)
  },
  cancel_oauth_login: async () => {
    localStorage.removeItem(OAUTH_REDIRECT_URI_KEY)
  },
  // Not a Tauri command — only called from `app/pages/auth/callback.vue` after the browser-mode
  // redirect above lands back with a `code`.
  complete_oauth_login: async (args) => {
    const redirectUri = localStorage.getItem(OAUTH_REDIRECT_URI_KEY) ?? `${window.location.origin}/auth/callback`
    const auth = await restRequest<AuthResponse>("/v1/anonymous/auth", {
      method: "POST",
      auth: false,
      body: { code: args.code, redirect_uri: redirectUri },
    })
    saveSession(auth)
    return auth
  },
  is_authenticated: async () => loadSession() !== null,
  logout: async () => {
    const session = loadSession()
    if (session) {
      await restRequest("/v1/users/logout", {
        method: "POST",
        auth: false,
        body: { refresh_token: session.refresh_token },
      }).catch(() => undefined)
    }
    clearSession()
  },
  refresh_token: async () => {
    const token = await refreshAccessToken()
    if (!token) throw new Error("Not authenticated")
    return loadSession()
  },
  get_access_token_bridge: async () => (await currentAccessToken()) ?? "",

  // --- users ---
  get_me: () => restRequest("/v1/users/me"),
  update_user: (a) => restRequest("/v1/users/me", { method: "POST", body: a.request }),
  update_user_status: (a) =>
    restRequest("/v1/users/me/status", { method: "POST", body: { user_status: USER_STATUS_BY_CODE[a.status as number] } }),
  initiate_avatar_upload: (a) =>
    restRequest("/v1/users/me/avatar/upload", {
      method: "POST",
      body: { content_type: a.contentType, size_bytes: a.sizeBytes },
    }),
  confirm_avatar_upload: (a) => restRequest("/v1/users/me/avatar/confirm", { method: "POST", body: { avatar_id: a.avatarId } }),
  initiate_banner_upload: (a) =>
    restRequest("/v1/users/me/banner/upload", {
      method: "POST",
      body: { content_type: a.contentType, size_bytes: a.sizeBytes },
    }),
  confirm_banner_upload: (a) => restRequest("/v1/users/me/banner/confirm", { method: "POST", body: { banner_id: a.bannerId } }),
  delete_banner: () => restRequest("/v1/users/me/banner", { method: "DELETE" }),

  // --- groups ---
  get_user_groups: async () => (await restRequest<{ groups: unknown[] }>("/v1/groups")).groups,
  create_group: (a) => restRequest("/v1/groups", { method: "POST", body: payload(a) }),
  get_group: async (a) => (await restRequest<{ group: unknown }>(`/v1/groups/${a.id}`)).group,
  update_group: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.id}`, { method: "POST", body: p })
  },
  delete_group: (a) => restRequest(`/v1/groups/${a.id}:delete`, { method: "POST" }),
  // REST returns proto JSON (camelCase, enums as names) — the store normalizes either shape.
  get_group_notification_settings: (a) => restRequest(`/v1/groups/${a.groupId}/notification-settings`),
  update_group_notification_settings: (a) => {
    const p = payload(a)
    const body: Args = { target_id: p.target_id, target_type: p.target_type }
    for (const key of ["level", "mute", "suppress_everyone", "suppress_roles"]) {
      if (p[key] != null) body[key] = p[key]
    }
    return restRequest(`/v1/groups/${p.group_id}/notification-settings`, { method: "PATCH", body })
  },

  // `GroupMember` nests the profile under `user` and keeps `role_ids` alongside it (see
  // docs.iopta.org/docs/api/introduction/groups#groupmember) — flatten to match the frontend's
  // `GroupUser` shape, which is a single flat object (see `app/types/groups.ts`).
  get_group_users: async (a) => {
    const { members, next_page_token } = await restRequest<{
      members?: Array<{ user: Record<string, unknown>; role_ids: unknown }>
      next_page_token?: string
    }>(`/v1/groups/${a.groupId}/users${pageQuery(a)}`)
    return {
      users: (members ?? []).map((m) => ({ ...m.user, role_ids: m.role_ids })),
      next_page_token: next_page_token ?? "",
    }
  },
  get_channel_member_list: async (a) => {
    const params = `offset=${encodeURIComponent(String(a.offset ?? 0))}&limit=${encodeURIComponent(String(a.limit ?? 0))}`
    const res = await restRequest<{
      sections?: Array<{ key: string; role_id?: string; count?: number }>
      items?: Array<{ member?: { user: Record<string, unknown>; role_ids: unknown }; section_key: string }>
      total?: number
      offline_hidden?: boolean
    }>(`/v1/groups/${a.groupId}/channels/${a.channelId}/members?${params}`)
    return {
      sections: (res.sections ?? []).map((s) => ({ key: s.key, role_id: s.role_id ?? null, count: s.count ?? 0 })),
      items: (res.items ?? [])
        .filter((i) => i.member)
        .map((i) => ({ user: { ...i.member!.user, role_ids: i.member!.role_ids }, section_key: i.section_key })),
      total: res.total ?? 0,
      offline_hidden: res.offline_hidden ?? false,
    }
  },
  search_group_users: async (a) => {
    const params = `query=${encodeURIComponent(String(a.query ?? ""))}&page_size=${encodeURIComponent(String(a.limit ?? 20))}`
    const { members } = await restRequest<{
      members?: Array<{ user: Record<string, unknown>; role_ids: unknown }>
    }>(`/v1/groups/${a.groupId}/users?${params}`)
    return (members ?? []).map((m) => ({ ...m.user, role_ids: m.role_ids }))
  },
  kick_user: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.group_id}/users/${p.user_id}:kick`, { method: "POST" })
  },
  leave_group: (a) => restRequest(`/v1/groups/${payload(a).group_id}:leave`, { method: "POST" }),

  get_group_roles: async (a) => {
    const { roles } = await restRequest<{ roles: Array<Record<string, unknown>> }>(`/v1/groups/${a.groupId}/roles`)
    return roles.map(normalizeRole)
  },
  create_role: async (a) => {
    const p = payload(a)
    return normalizeRole(await restRequest(`/v1/groups/${p.group_id}/roles`, { method: "POST", body: p }))
  },
  update_role: async (a) => {
    const p = payload(a)
    return normalizeRole(await restRequest(`/v1/groups/roles/${p.role_id}:update`, { method: "POST", body: p }))
  },
  // Unlike Tauri's `move_role` (which echoes back the full, unchanged-except-for-order Role), the
  // REST endpoint only returns `{}` — `groups.ts`'s `moveRole` store action accounts for this by
  // splicing its own pre-call snapshot of the role back in rather than relying on this response.
  move_role: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/roles/${p.role_id}:move`, { method: "POST", body: p })
  },
  delete_role: (a) => restRequest(`/v1/groups/roles/${a.roleId}:delete`, { method: "POST" }),
  assign_role: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.group_id}/roles/${p.role_id}:assign`, { method: "POST", body: { target_user_id: p.target_user_id } })
  },
  remove_role: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.group_id}/roles/${p.role_id}:remove`, { method: "POST", body: { target_user_id: p.target_user_id } })
  },

  create_category: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.group_id}/categories`, { method: "POST", body: p })
  },
  update_category: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/categories/${p.id}:update`, { method: "POST", body: p })
  },
  delete_category: (a) => restRequest(`/v1/groups/categories/${a.id}:delete`, { method: "POST" }),
  move_category: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/categories/${p.category_id}:move`, { method: "POST", body: p })
  },

  create_channel: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.category_id}/channels`, { method: "POST", body: p })
  },
  delete_channel: (a) => restRequest(`/v1/groups/channels/${a.id}:delete`, { method: "POST" }),
  move_channel: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/channels/${p.channel_id}:move`, { method: "POST", body: p })
  },
  set_channel_category_sync: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/channels/${p.channel_id}:set-category-sync`, { method: "POST", body: p })
  },

  create_invite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/${p.group_id}/invites`, { method: "POST", body: p })
  },
  get_group_invites: async (a) => {
    const { invites, next_page_token } = await restRequest<{ invites?: unknown[]; next_page_token?: string }>(
      `/v1/groups/${a.groupId}/invites${pageQuery(a)}`,
    )
    return { invites: invites ?? [], next_page_token: next_page_token ?? "" }
  },
  // `GroupBan.user` is a plain `common.User` (a banned user is no longer a member, so there are
  // no roles) — `role_ids` is filled in to match the frontend's `GroupUser` shape.
  get_group_bans: async (a) => {
    const { bans, next_page_token } = await restRequest<{
      bans?: Array<{ user?: Record<string, unknown> } & Record<string, unknown>>
      next_page_token?: string
    }>(`/v1/groups/${a.groupId}/bans${pageQuery(a)}`)
    return {
      bans: (bans ?? []).map((b) => ({ ...b, user: { ...b.user, role_ids: [] } })),
      next_page_token: next_page_token ?? "",
    }
  },
  update_invite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/invites/${p.id}`, { method: "POST", body: p })
  },
  delete_invite: (a) => restRequest(`/v1/groups/invites/${a.id}:delete`, { method: "POST" }),
  join_by_invite: (a) => restRequest("/v1/groups/join", { method: "POST", body: payload(a) }),

  get_channel_permission_overwrites: async (a) =>
    (await restRequest<{ overwrites: unknown[] }>(`/v1/groups/channels/${a.channelId}/permission-overwrites`)).overwrites,
  set_channel_permission_overwrite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/channels/${p.channel_id}/permission-overwrites:set`, { method: "POST", body: p })
  },
  delete_channel_permission_overwrite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/channels/${p.channel_id}/permission-overwrites:delete`, { method: "POST", body: p })
  },
  get_category_permission_overwrites: async (a) =>
    (await restRequest<{ overwrites: unknown[] }>(`/v1/groups/categories/${a.categoryId}/permission-overwrites`)).overwrites,
  set_category_permission_overwrite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/categories/${p.category_id}/permission-overwrites:set`, { method: "POST", body: p })
  },
  delete_category_permission_overwrite: (a) => {
    const p = payload(a)
    return restRequest(`/v1/groups/categories/${p.category_id}/permission-overwrites:delete`, { method: "POST", body: p })
  },

  initiate_group_avatar_upload: (a) =>
    restRequest(`/v1/groups/${a.groupId}/avatar/upload`, {
      method: "POST",
      body: { content_type: a.contentType, size_bytes: a.sizeBytes },
    }),
  confirm_group_avatar_upload: (a) =>
    restRequest(`/v1/groups/${a.groupId}/avatar/confirm`, { method: "POST", body: { avatar_id: a.avatarId } }),
  initiate_group_banner_upload: (a) =>
    restRequest(`/v1/groups/${a.groupId}/banner/upload`, {
      method: "POST",
      body: { content_type: a.contentType, size_bytes: a.sizeBytes },
    }),
  confirm_group_banner_upload: (a) =>
    restRequest(`/v1/groups/${a.groupId}/banner/confirm`, { method: "POST", body: { banner_id: a.bannerId } }),

  // --- messages ---
  get_channel_messages: async (a) =>
    (
      await restRequest<{ messages: unknown[] }>(`/v1/messages/channel/${a.channelId}`, {
        query: { limit: a.limit, before_id: a.beforeId },
      })
    ).messages,
  create_message: (a) => restRequest("/v1/messages", { method: "POST", body: payload(a) }),
  update_message: (a) => {
    const p = payload(a)
    return restRequest(`/v1/messages/${p.id}`, { method: "POST", body: p })
  },
  delete_message: (a) => restRequest(`/v1/messages/${a.id}:delete`, { method: "POST" }),
  typing: (a) => {
    const p = payload(a)
    return restRequest(`/v1/messages/channel/${p.channel_id}/typing`, { method: "POST", body: { is_typing: p.is_typing } })
  },
  add_reaction: (a) => {
    const p = payload(a)
    return restRequest(`/v1/messages/${p.message_id}/reactions`, { method: "POST", body: { emoji: p.emoji } })
  },
  remove_reaction: (a) => {
    const p = payload(a)
    return restRequest(`/v1/messages/${p.message_id}/reactions:remove`, { method: "POST", body: { emoji: p.emoji } })
  },
  get_message: (a) => restRequest(`/v1/messages/${a.messageId}`),
  forward_message: (a) => restRequest("/v1/messages:forward", { method: "POST", body: payload(a) }),
  get_messages_around: (a) =>
    restRequest(`/v1/messages/channel/${a.channelId}/around/${a.messageId}`, {
      query: { before: a.before, after: a.after },
    }),
  // Same plain-word filters as the Rust command (`SearchMessagesRequestDto`), turned into the proto
  // enum names; repeated fields repeat their key, which `restRequest`'s own `query` can't express.
  search_messages: async (a) => {
    const p = payload(a) as {
      group_id: string, query?: string, channel_ids?: string[], author_ids?: string[], has?: string[],
      before?: string | null, after?: string | null, sort?: string | null, page_size?: number | null, page_token?: string | null,
    }
    const params = new URLSearchParams()
    if (p.query) params.append("query", p.query)
    for (const id of p.channel_ids ?? []) params.append("channelIds", id)
    for (const id of p.author_ids ?? []) params.append("authorIds", id)
    for (const h of p.has ?? []) params.append("has", `SEARCH_MESSAGES_HAS_${h.toUpperCase()}`)
    if (p.before) params.append("before", p.before)
    if (p.after) params.append("after", p.after)
    if (p.sort) params.append("sort", `SEARCH_MESSAGES_SORT_${p.sort.toUpperCase()}`)
    if (p.page_size) params.append("pageSize", String(p.page_size))
    if (p.page_token) params.append("pageToken", p.page_token)
    const res = await restRequest<{ hits?: Array<{ message?: unknown, highlights?: string[] }>, total?: number | string, next_page_token?: string }>(
      `/v1/groups/${p.group_id}/messages/search?${params}`,
    )
    return {
      hits: (res.hits ?? []).map((h) => ({ message: h.message ?? null, highlights: h.highlights ?? [] })),
      total: Number(res.total ?? 0),
      next_page_token: res.next_page_token ?? "",
    }
  },

  // --- channels ---
  start_channel_connection: (a) => restRequest(`/v1/channels/${a.channelId}/connect`, { method: "POST" }),
  update_channel: async (a) => {
    const p = payload(a)
    const res = await restRequest<{ success: boolean }>(`/v1/channels/${p.channel_id}`, { method: "POST", body: p })
    return res.success
  },

  // --- subscriptions (proto/subscriptions.proto, google.api.http annotations) ---
  get_subscription_plans: () => restRequest("/v1/subscriptions/plans"),
  get_my_subscription: () => restRequest("/v1/subscriptions/me"),
  create_subscription_payment: (a) => restRequest("/v1/subscriptions/payments", { method: "POST", body: payload(a) }),
  get_subscription_payment: (a) => restRequest(`/v1/subscriptions/payments/${a.paymentId}`),
  set_subscription_auto_renew: (a) =>
    restRequest("/v1/subscriptions/me/auto-renew", { method: "POST", body: { auto_renew: a.autoRenew } }),
  activate_demo: () => restRequest("/v1/subscriptions/demo", { method: "POST", body: {} }),
  // int64 fields arrive as JSON strings over REST; the Tauri path hands back numbers.
  get_my_credits: async () => {
    const res = await restRequest<Record<string, unknown>>("/v1/subscriptions/credits/me?history_limit=0")
    return {
      plan_balance_micro: Number(res.plan_balance_micro ?? 0),
      plan_expires_at: res.plan_expires_at ?? null,
      purchased_balance_micro: Number(res.purchased_balance_micro ?? 0),
      total_balance_micro: Number(res.total_balance_micro ?? 0),
      can_spend: !!res.can_spend,
      can_purchase: !!res.can_purchase,
      micro_per_credit: Number(res.micro_per_credit ?? 1_000_000),
    }
  },

  get_ai_models: async () => {
    const res = await restRequest<{ models?: Array<Record<string, unknown>>, micro_per_credit?: unknown }>("/v1/subscriptions/ai/models")
    return {
      models: (res.models ?? []).map((m) => ({
        provider: String(m.provider ?? ""),
        model: String(m.model ?? ""),
        display_name: String(m.display_name ?? ""),
        input_micro_per_1m: Number(m.input_micro_per_1m ?? 0),
        output_micro_per_1m: Number(m.output_micro_per_1m ?? 0),
      })),
      micro_per_credit: Number(res.micro_per_credit ?? 1_000_000),
    }
  },
  get_ai_usage: async (a) => {
    const res = await restRequest<{ items?: Array<Record<string, unknown>>, [k: string]: unknown }>("/v1/subscriptions/ai/usage", {
      query: { limit: a.limit, agent_id: a.agentId, from: a.from, to: a.to, time_zone: a.timeZone, cursor: a.cursor },
    })
    const legacyTotals = {
      requests: (res.items ?? []).length,
      input_tokens: res.last_30_days_input_tokens,
      output_tokens: res.last_30_days_output_tokens,
      charged_micro: res.last_30_days_charged_micro,
    }
    return {
      items: (res.items ?? []).map((i) => ({
        id: String(i.id ?? ""),
        agent_id: String(i.agent_id ?? ""),
        provider: String(i.provider ?? ""),
        model: String(i.model ?? ""),
        key_source: String(i.key_source ?? ""),
        input_tokens: Number(i.input_tokens ?? 0),
        output_tokens: Number(i.output_tokens ?? 0),
        cost_micro: Number(i.cost_micro ?? 0),
        charged_micro: Number(i.charged_micro ?? 0),
        status: String(i.status ?? ""),
        created_at: i.created_at ?? null,
      })),
      last_30_days_input_tokens: Number(res.last_30_days_input_tokens ?? 0),
      last_30_days_output_tokens: Number(res.last_30_days_output_tokens ?? 0),
      last_30_days_charged_micro: Number(res.last_30_days_charged_micro ?? 0),
      micro_per_credit: Number(res.micro_per_credit ?? 1_000_000),
      totals: usageTotals(res.totals ?? legacyTotals),
      days: asRecords(res.days).map((d) => ({ day: String(d.day ?? ""), totals: usageTotals(d.totals) })),
      by_agent: asRecords(res.by_agent).map((g) => ({ agent_id: String(g.agent_id ?? ""), totals: usageTotals(g.totals) })),
      by_model: asRecords(res.by_model).map((g) => ({
        provider: String(g.provider ?? ""),
        model: String(g.model ?? ""),
        totals: usageTotals(g.totals),
      })),
      next_cursor: (res.next_cursor as string | undefined) || null,
      from: res.from ?? null,
      to: res.to ?? null,
      time_zone: String(res.time_zone ?? ""),
    }
  },
  get_notification_settings: async () => {
    const res = await restRequest<Record<string, unknown>>("/v1/subscriptions/notifications")
    return { credit_alerts_enabled: !!res.credit_alerts_enabled }
  },
  set_notification_settings: async (a) => {
    const res = await restRequest<Record<string, unknown>>("/v1/subscriptions/notifications", {
      method: "POST",
      body: { credit_alerts_enabled: a.creditAlertsEnabled },
    })
    return { credit_alerts_enabled: !!res.credit_alerts_enabled }
  },

  // --- agent tasks (proto/agent_tasks.proto, google.api.http annotations) ---
  get_agent_task_settings: async () => {
    const res = await restRequest<Record<string, unknown>>("/v1/agent-credentials/tasks/settings")
    return {
      max_tasks_per_agent: Number(res.max_tasks_per_agent ?? 0),
      min_interval_seconds: Number(res.min_interval_seconds ?? 0),
      max_title_length: Number(res.max_title_length ?? 0),
      max_prompt_length: Number(res.max_prompt_length ?? 0),
      default_time_zone: String(res.default_time_zone ?? ""),
      history_retention_days: Number(res.history_retention_days ?? 0),
    }
  },
  list_agent_tasks: async (a) => {
    const res = await restRequest<{ tasks?: unknown }>(`/v1/agent-credentials/${a.botId}/tasks`, {
      query: { include_finished: a.includeFinished ? true : undefined },
    })
    return { tasks: asRecords(res.tasks).map(normalizeAgentTask) }
  },
  get_agent_task: async (a) =>
    normalizeAgentTask(await restRequest(`/v1/agent-credentials/${a.botId}/tasks/${a.taskId}`)),
  create_agent_task: async (a) => {
    const p = payload(a)
    return normalizeAgentTask(await restRequest(`/v1/agent-credentials/${p.bot_id}/tasks`, { method: "POST", body: p }))
  },
  update_agent_task: async (a) => {
    const p = payload(a)
    return normalizeAgentTask(
      await restRequest(`/v1/agent-credentials/${p.bot_id}/tasks/${p.task_id}:update`, { method: "POST", body: p }),
    )
  },
  set_agent_task_state: async (a) =>
    normalizeAgentTask(
      await restRequest(`/v1/agent-credentials/${a.botId}/tasks/${a.taskId}:state`, {
        method: "POST",
        body: { action: a.action },
      }),
    ),
  delete_agent_task: async (a) => {
    await restRequest(`/v1/agent-credentials/${a.botId}/tasks/${a.taskId}:delete`, { method: "POST", body: {} })
  },
  get_agent_task_runs: async (a) => {
    const res = await restRequest<Record<string, unknown>>(`/v1/agent-credentials/${a.botId}/tasks/${a.taskId}/runs`, {
      query: { limit: a.limit, before: a.before },
    })
    return {
      runs: asRecords(res.runs).map((r) => ({
        scheduled_at: r.scheduled_at ?? null,
        run_id: String(r.run_id ?? ""),
        status: enumValue(r.status, AGENT_TASK_RUN_STATUS_NAMES),
        started_at: r.started_at ?? null,
        finished_at: r.finished_at ?? null,
        error: r.error ?? null,
        input_tokens: Number(r.input_tokens ?? 0),
        output_tokens: Number(r.output_tokens ?? 0),
        charged_micro: Number(r.charged_micro ?? 0),
      })),
      next_before: res.next_before ?? null,
      micro_per_credit: Number(res.micro_per_credit ?? 1_000_000),
    }
  },

  // --- agent tool approvals (proto/agent_credentials.proto AgentToolApprovalsApi) ---
  list_agent_tool_approvals: async (a) => {
    const res = await restRequest<{ approvals?: unknown }>("/v1/agent-approvals", {
      query: { bot_id: a.botId || undefined },
    })
    return { approvals: asRecords(res.approvals).map(normalizeAgentToolApproval) }
  },
  decide_agent_tool_approval: async (a) =>
    normalizeAgentToolApproval(
      await restRequest(`/v1/agent-approvals/${a.approvalId}:decide`, {
        method: "POST",
        body: { decision: a.decision },
      }),
    ),

  // --- friends ---
  send_friend_request: (a) => restRequest("/v1/friends/requests", { method: "POST", body: payload(a) }),
  accept_friend_request: (a) => restRequest(`/v1/friends/requests/${a.friendId}:accept`, { method: "POST" }),
  decline_friend_request: (a) => restRequest(`/v1/friends/requests/${a.friendId}:decline`, { method: "POST" }),
  cancel_friend_request: (a) => restRequest(`/v1/friends/requests/${a.friendId}:cancel`, { method: "POST" }),
  remove_friend: (a) => restRequest(`/v1/friends/${a.friendId}:remove`, { method: "POST" }),
  get_friends: (a) => restRequest("/v1/friends", { query: { limit: a.limit, before_id: a.beforeId } }),
  get_incoming_requests: (a) => restRequest("/v1/friends/requests/incoming", { query: { limit: a.limit, before_id: a.beforeId } }),
  get_outgoing_requests: (a) => restRequest("/v1/friends/requests/outgoing", { query: { limit: a.limit, before_id: a.beforeId } }),
  get_blocked_users: (a) => restRequest("/v1/friends/blocked", { query: { limit: a.limit, before_id: a.beforeId } }),
  get_friendship_status: (a) => restRequest(`/v1/friends/status/${a.userId}`),
  block_user: (a) => restRequest("/v1/friends/blocked", { method: "POST", body: payload(a) }),
  unblock_user: (a) => restRequest(`/v1/friends/blocked/${a.userId}:unblock`, { method: "POST" }),

  // --- attachments ---
  initiate_upload: (a) => restRequest("/v1/attachments/upload", { method: "POST", body: payload(a) }),
  confirm_upload: (a) => restRequest(`/v1/attachments/${a.attachmentId}/confirm`, { method: "POST" }),
  initiate_multipart_upload: (a) => restRequest("/v1/attachments/multipart", { method: "POST", body: payload(a) }),
  get_multipart_part_urls: (a) =>
    restRequest(`/v1/attachments/${a.attachmentId}/multipart/part-urls`, { query: { part_numbers: a.partNumbers } }),
  complete_multipart_upload: (a) =>
    restRequest(`/v1/attachments/${a.attachmentId}/multipart/complete`, { method: "POST", body: { parts: a.parts } }),
  abort_multipart_upload: (a) => restRequest(`/v1/attachments/${a.attachmentId}/multipart/abort`, { method: "POST" }),
  get_attachment: (a) => restRequest(`/v1/attachments/${a.attachmentId}`),
  get_download_url: (a) => restRequest(`/v1/attachments/${a.attachmentId}/download-url`),
  delete_attachment: (a) => restRequest(`/v1/attachments/${a.attachmentId}:delete`, { method: "POST" }),

  // --- bots (docs.iopta.org/docs/api/introduction/bots — not linked from the intro nav, but
  // documented) ---
  // `GET /v1/bots` returns `{ bots: Bot[] }` — a flat list (`GetMyBotsResponse.bots` is
  // `repeated common.Bot` in user_bots.proto, unlike `GetBot`/`UpdateBot`'s single-bot
  // `BotResponse { bot, credential? }` wrapper below) — no unwrapping needed, matches what
  // `useBots.ts`'s `getMyBots` wants already.
  get_my_bots: async () => (await restRequest<{ bots: unknown[] }>("/v1/bots")).bots,
  // Response is already `{ bot, credential? }` — exactly `BotResponse`, the shape `useBots.ts`
  // expects — no unwrapping needed.
  get_bot: (a) => restRequest(`/v1/bots/${a.botId}`),
  create_bot: (a) => restRequest("/v1/bots", { method: "POST", body: payload(a) }),
  // Unlike `create_bot` (whose `{ bot, token }` response is already what the frontend wants),
  // `update_bot` responds with `{ bot: Bot }` but `useBots.ts` wants the `Bot` itself.
  update_bot: async (a) => {
    const p = payload(a)
    const { bot } = await restRequest<{ bot: unknown }>(`/v1/bots/${p.bot_id}`, { method: "POST", body: p })
    return bot
  },
  delete_bot: (a) => restRequest(`/v1/bots/${a.botId}:delete`, { method: "POST" }),
  regenerate_bot_token: async (a) => (await restRequest<{ token: string }>(`/v1/bots/${a.botId}:regenerate-token`, { method: "POST" })).token,
  add_bot_to_group: (a) => {
    const p = payload(a)
    return restRequest(`/v1/bots/${p.bot_id}/groups/${p.group_id}`, { method: "POST", body: { permissions: p.permissions } })
  },
  get_available_intents: async () => (await restRequest<{ intents: unknown[] }>("/v1/bots/available-intents")).intents,
  initiate_bot_avatar_upload: (a) =>
    restRequest(`/v1/bots/${a.botId}/avatar/upload`, {
      method: "POST",
      body: { content_type: a.contentType, size_bytes: a.sizeBytes },
    }),
  // Both respond with `{ bot: Bot }` (`BotResponse`), same as `update_bot` above — unwrap to just `Bot`.
  confirm_bot_avatar_upload: async (a) => {
    const { bot } = await restRequest<{ bot: unknown }>(`/v1/bots/${a.botId}/avatar/confirm`, {
      method: "POST",
      body: { avatar_id: a.avatarId },
    })
    return bot
  },
  delete_bot_avatar: async (a) => {
    const { bot } = await restRequest<{ bot: unknown }>(`/v1/bots/${a.botId}/avatar`, { method: "DELETE" })
    return bot
  },

  // --- interactions (docs.iopta.org/docs/api/introduction/interactions — also undocumented from
  // the intro nav) ---
  get_bot_commands: async (a) => (await restRequest<{ commands: unknown[] }>(`/v1/interactions/bots/${a.botId}/commands`)).commands,
  get_group_commands: async (a) =>
    (await restRequest<{ commands: unknown[] }>(`/v1/interactions/groups/${a.groupId}/commands`)).commands,
  register_command: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/commands`, { method: "POST", body: p })
  },
  update_command: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/commands/${p.command_id}`, { method: "POST", body: p })
  },
  delete_command: (a) => restRequest(`/v1/interactions/bots/${a.botId}/commands/${a.commandId}:delete`, { method: "POST" }),
  execute_command: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/commands/${p.command_id}:execute`, { method: "POST", body: p })
  },
  request_autocomplete: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/commands/${p.command_id}:autocomplete`, { method: "POST", body: p })
  },
  invoke_message_component: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/messages/${p.message_id}:invoke-component`, { method: "POST", body: p })
  },
  submit_modal: (a) => {
    const p = payload(a)
    return restRequest(`/v1/interactions/bots/${p.bot_id}/modals:submit`, { method: "POST", body: p })
  },

  // --- boards (proto/boards.proto) — see boardRoutes.ts ---
  ...boardRoutes,
}

export function resolveRoute(cmd: string): RouteHandler {
  const handler = routes[cmd]
  if (!handler) throw new BrowserUnsupportedCommandError(cmd)
  return handler
}
